import React, { useEffect, useState, useCallback } from 'react';
import { Images, Trash2, Loader2, Wand2 } from 'lucide-react';
import { fetchGallery, deleteGalleryItems, fetchGenerationSession } from '../services/api';
import type { GalleryItem } from '../services/api';
import GalleryLightbox from './QuickGen/components/GalleryLightbox';
import { BTN, STATUS } from '../styles/tokens';
import ConfirmDialog from '../components/ConfirmDialog';
import { getThumbnailUrl } from '../utils/image';
import { useGenContext } from '../contexts/GenContext';
import { useToast } from '../hooks/useToast';
import { RunningLoader } from '@/components/RunningLoader';

const TYPE_LABEL: Record<string, string> = {
  generated: 'AI 快速生成',
  'style-transfer': '人像風格',
  agent: '協作聊天',
  upload: '上傳',
  'remove-bg': '去背',
  'stamp-edit': '使用者編輯',
};

const ImageGallery: React.FC = () => {
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [lightbox, setLightbox] = useState<GalleryItem | null>(null);

  // 選擇模式
  const [isSelecting, setIsSelecting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // 繼續生成
  const { startGenerationRestore } = useGenContext();
  const { showToast } = useToast();
  const [continuingId, setContinuingId] = useState<string | null>(null);

  /**
   * 從這張圖回到它那次的生成流程。
   * 只是把資料撈回來套用到 Result 頁 —— 沒有按下生成前不會產生任何新紀錄。
   */
  const handleContinueGeneration = async (item: GalleryItem) => {
    if (!item.generationSessionId || continuingId) return;
    setContinuingId(item.id);
    try {
      const session = await fetchGenerationSession(item.generationSessionId);
      startGenerationRestore(session, item.id, item.createdAt);
    } catch (e) {
      console.error(e);
      showToast('讀取生成紀錄失敗，請稍後再試');
    } finally {
      setContinuingId(null);
    }
  };

  const load = useCallback(async (p: number) => {
    setIsSelecting(false);
    setSelected(new Set());
    setLoading(true);
    try {
      const res = await fetchGallery(p, 12);
      setItems(res.data);
      setTotalPages(res.pagination.totalPages);
      setTotal(res.pagination.total);
      setPage(p);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(1); }, [load]);

  const lightboxIndex = lightbox ? items.findIndex(i => i.id === lightbox.id) : -1;
  const prevLightbox = () => { if (lightboxIndex > 0) setLightbox(items[lightboxIndex - 1]); };
  const nextLightbox = () => { if (lightboxIndex < items.length - 1) setLightbox(items[lightboxIndex + 1]); };

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleBatchDelete = async () => {
    if (selected.size === 0) return;
    setIsDeleting(true);
    try {
      await deleteGalleryItems([...selected]);
      setItems(prev => prev.filter(i => !selected.has(i.id)));
      setTotal(prev => prev - selected.size);
      setSelected(new Set());
      setIsSelecting(false);
    } catch (e) {
      console.error(e);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          {/* 手機隱藏主標題（已由頂部列顯示） */}
          <h2 className="hidden lg:flex text-xl md:text-2xl font-bold text-slate-900 dark:text-white items-center gap-2">
            <Images size={24} className="text-blue-500" />
            圖片庫
          </h2>
          {!loading && (
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">共 {total} 張生成圖片</p>
          )}
        </div>

        {/* 選擇模式操作 or 垃圾桶按鈕 */}
        {items.length > 0 && (
          isSelecting ? (
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setIsSelecting(false); setSelected(new Set()); }}
                className="px-3 py-1.5 rounded-lg text-sm border border-slate-300 bg-slate-200 text-slate-800 hover:bg-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600 transition"
              >
                取消
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={selected.size === 0 || isDeleting}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm ${BTN.danger}`}
              >
                <Trash2 size={14} />
                {`刪除（${selected.size}）`}
              </button>
            </div>
          ) : (
            <button
              onClick={() => setIsSelecting(true)}
              className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
              title="批量刪除"
            >
              <Trash2 size={20} />
            </button>
          )
        )}
      </div>

      {/* 選擇模式 banner */}
      {isSelecting && (
        <div className={`mb-4 flex items-center gap-2 px-3 py-2 rounded-lg ${STATUS.warning} text-sm`}>
          <span>⚠</span>
          <span>選擇模式：僅限本頁圖片，選擇不可跨頁。點擊縮圖勾選，再按「刪除」移至垃圾桶。</span>
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-32 text-slate-500 dark:text-slate-400">
          <RunningLoader size="lg" />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-32 text-slate-400 dark:text-slate-500">
          <Images size={64} strokeWidth={1} />
          <p className="mt-4 text-lg">還沒有生成過圖片</p>
          <p className="text-sm mt-1">使用 AI 快速生成或人像風格生成後，圖片會出現在這裡</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {items.map(item => {
              const isChecked = selected.has(item.id);
              return (
                <div
                  key={item.id}
                  className={`group relative aspect-square rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-800 cursor-pointer shadow transition-shadow
                    ${isSelecting
                      ? isChecked
                        ? 'ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-slate-900 shadow-lg'
                        : 'hover:shadow-lg'
                      : 'hover:shadow-lg'
                    }`}
                  onClick={() => {
                    if (isSelecting) toggleSelect(item.id);
                    else setLightbox(item);
                  }}
                >
                  <img
                    src={getThumbnailUrl(item)}
                    alt=""
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />

                  {/* 選擇模式：右上角勾選框 */}
                  {isSelecting && (
                    <div className={`absolute top-2 right-2 w-5 h-5 rounded flex items-center justify-center border-2 transition-colors
                      ${isChecked
                        ? 'bg-blue-500 border-blue-500 text-white'
                        : 'bg-white/80 border-slate-400'
                      }`}
                    >
                      {isChecked && (
                        <svg viewBox="0 0 12 12" fill="none" className="w-3 h-3">
                          <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                  )}

                  {/* 正常模式：單張圖片的「繼續生成」入口。沒有生成紀錄的舊圖不顯示 */}
                  {!isSelecting && item.generationSessionId && (
                    <button
                      onClick={(e) => { e.stopPropagation(); void handleContinueGeneration(item); }}
                      disabled={continuingId !== null}
                      title="從這張圖繼續生成"
                      className="absolute top-2 right-2 z-10 flex items-center gap-1 px-2 py-1 rounded-lg bg-white/90 dark:bg-slate-900/90 text-slate-700 dark:text-slate-200 text-[11px] font-semibold shadow hover:bg-white dark:hover:bg-slate-800 disabled:opacity-50 transition"
                    >
                      {continuingId === item.id
                        ? <Loader2 size={12} className="animate-spin" />
                        : <Wand2 size={12} />}
                      繼續生成
                    </button>
                  )}

                  {/* 正常模式：hover 顯示標籤 */}
                  {!isSelecting && (
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-end">
                      <div className="w-full p-2 translate-y-full group-hover:translate-y-0 transition-transform">
                        <span className="text-[11px] bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded px-2 py-0.5">
                          {TYPE_LABEL[item.source] ?? item.source}
                        </span>
                        <p className="text-[10px] text-white/70 mt-0.5">
                          {new Date(item.createdAt).toLocaleDateString('zh-TW')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* 分頁 */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-8">
              <button
                onClick={() => load(page - 1)}
                disabled={page <= 1}
                className="px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 disabled:opacity-40 hover:bg-slate-200 dark:hover:bg-slate-700 transition"
              >
                上一頁
              </button>
              <span className="text-sm text-slate-500 dark:text-slate-400">{page} / {totalPages}</span>
              <button
                onClick={() => load(page + 1)}
                disabled={page >= totalPages}
                className="px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 disabled:opacity-40 hover:bg-slate-200 dark:hover:bg-slate-700 transition"
              >
                下一頁
              </button>
            </div>
          )}
        </>
      )}

      {/* 批量刪除確認 */}
      {showDeleteConfirm && (
        <ConfirmDialog
          title={`確認刪除 ${selected.size} 張圖片`}
          message="選取的圖片將移至垃圾桶，確定要刪除嗎？"
          onConfirm={async () => {
            await handleBatchDelete();
            setShowDeleteConfirm(false);
          }}
          onCancel={() => setShowDeleteConfirm(false)}
          isLoading={isDeleting}
        />
      )}

      {/* Lightbox */}
      {lightbox && (
        <GalleryLightbox
          item={lightbox}
          hasPrev={lightboxIndex > 0}
          hasNext={lightboxIndex < items.length - 1}
          onClose={() => setLightbox(null)}
          onPrev={prevLightbox}
          onNext={nextLightbox}
          onDeleted={(id) => {
            setItems(prev => prev.filter(i => i.id !== id));
            setTotal(prev => prev - 1);
            setLightbox(null);
          }}
          onSaved={(newItem) => {
            setItems(prev => [newItem, ...prev]);
            setTotal(prev => prev + 1);
          }}
        />
      )}
    </div>
  );
};

export default ImageGallery;
